import React from "react";
import { motion } from "framer-motion";

interface LoadingStateProps {
  message?: string;
  size?: "small" | "medium" | "large";
  padding?: "none" | "normal" | "large";
  fullPage?: boolean;
}

const LoadingState: React.FC<LoadingStateProps> = ({
  message = "Loading...",
  size = "medium",
  padding = "normal",
  fullPage = true,
}) => {
  const isDarkMode = document.documentElement.classList.contains("dark");
  const logoSrc = isDarkMode
    ? "/assets/ukti-logo-white.png"
    : "/assets/ukti-logo-no-text.png";

  // Increase size values for better visibility
  const sizeMap = {
    small: {
      logo: 40,
      container: "p-4",
      text: "text-base",
    },
    medium: {
      logo: 60,
      container: "p-6",
      text: "text-lg",
    },
    large: {
      logo: 80,
      container: "p-8",
      text: "text-xl",
    },
  };

  const paddingMap = {
    none: "",
    normal: "p-6",
    large: "p-10",
  };

  // Always center in the viewport with fixed positioning if fullPage
  const containerClasses = fullPage
    ? "fixed inset-0 z-50 flex items-center justify-center bg-[var(--background)]/80 backdrop-blur-md"
    : "flex items-center justify-center";

  return (
    <div className={`${containerClasses} ${paddingMap[padding]}`}>
      <motion.div
        className="flex flex-col items-center bg-[var(--card)] shadow-xl rounded-xl p-8 min-w-[250px] border border-[var(--border)]"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="relative">
          <motion.div
            className="relative z-10"
            animate={{
              opacity: [0.7, 1, 0.7],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <img
              src={logoSrc}
              alt="Loading..."
              height={sizeMap[size].logo}
              width={sizeMap[size].logo}
              className="object-contain"
            />
          </motion.div>
          <motion.div
            className="absolute -inset-4 z-0"
            style={{
              background: `radial-gradient(circle, var(--color-accent) 0%, transparent 70%)`,
              filter: "blur(10px)",
            }}
            animate={{
              opacity: [0.2, 0.4, 0.2],
              scale: [0.9, 1.1, 0.9],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
        {message && (
          <motion.p
            className={`mt-6 text-[var(--foreground)] font-medium ${sizeMap[size].text} text-center`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            {message}
          </motion.p>
        )}
      </motion.div>
    </div>
  );
};

export default LoadingState;
